import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Spinner,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon, DeleteIcon, EditIcon, SearchIcon } from "@chakra-ui/icons";
import PageHeader from "../components/PageHeader";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getActiveSeason,
  getMlbPool,
  getMlbPlayers,
  createMlbRoster,
  getMyMlbRosters,
  deleteMlbRoster,
  updateMlbRoster,
} from "../api/mlbHrDerby";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";
const HR_CAP       = 130;
const ROSTER_SIZE  = 6;
const TABLE_PAGE_SIZE = 50;

function fmtAvg(val) {
  if (val == null) return "—";
  return Number(val).toFixed(3).replace(/^0/, "");
}
function fmtSlg(val) {
  if (val == null) return "—";
  return Number(val).toFixed(3).replace(/^0/, "");
}

// ── Memoized player browser — only re-renders when its own props change ──────
const PlayerBrowserPanel = React.memo(function PlayerBrowserPanel({
  players, totalCount, selectedIdsSet, selectedIdsLength,
  activeTab, prevYearLabel, currYearLabel,
  sortKey, sortDir, searchQuery,
  page, totalPages, height,
  onTogglePlayer, onRemovePlayer, onToggleSort, onSearchChange, onTabChange, onPageChange,
}) {
  function sortInd(key) {
    if (sortKey !== key) return " ↕";
    return sortDir === "desc" ? " ↓" : " ↑";
  }

  return (
    <Box
      flex="3"
      minW="0"
      bg="white"
      color="black"
      border={`3px solid ${TOP_BAR_BLUE}`}
      borderRadius="lg"
      boxShadow="lg"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      height={height ? `${height}px` : undefined}
      maxH={height ? undefined : "80vh"}
    >
      <Tabs
        index={activeTab}
        onChange={onTabChange}
        colorScheme="blue"
        display="flex"
        flexDirection="column"
        flex="1"
        overflow="hidden"
      >
        {/* Header + tabs — fixed height */}
        <Box px={6} pt={5} pb={0} flexShrink={0}>
          <HStack justify="space-between" align="center" mb={4}>
            <Heading size="md" color={TOP_BAR_BLUE}>Player Browser</Heading>
            <InputGroup size="sm" maxW="260px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search name, team, position..."
                value={searchQuery}
                onChange={onSearchChange}
                bg="gray.50"
                borderColor="gray.200"
              />
            </InputGroup>
          </HStack>
          <TabList borderBottom="2px solid" borderColor="gray.200">
            <Tab fontWeight="semibold" _selected={{ color: TOP_BAR_BLUE, borderColor: TOP_BAR_BLUE }}>
              {prevYearLabel} Season
            </Tab>
            <Tab fontWeight="semibold" _selected={{ color: TOP_BAR_BLUE, borderColor: TOP_BAR_BLUE }}>
              {currYearLabel} Spring Training
            </Tab>
          </TabList>
        </Box>

        {/* Tab panels — fill remaining height, scroll internally */}
        <TabPanels flex="1" display="flex" flexDirection="column" overflow="hidden">
          {[0, 1].map((tabIdx) => (
            <TabPanel
              key={tabIdx}
              p={0}
              flex="1"
              display="flex"
              flexDirection="column"
              overflow="hidden"
            >
              <Box flex="1" overflowX="auto" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead position="sticky" top={0} bg="white" zIndex={1} boxShadow="0 1px 0 #e2e8f0">
                    <Tr>
                      <Th cursor="pointer" userSelect="none" w="180px" onClick={() => onToggleSort("name")} color={sortKey === "name" ? TOP_BAR_BLUE : undefined}>Player{sortInd("name")}</Th>
                      <Th cursor="pointer" userSelect="none" w="60px"  onClick={() => onToggleSort("team")} color={sortKey === "team" ? TOP_BAR_BLUE : undefined}>Team{sortInd("team")}</Th>
                      <Th cursor="pointer" userSelect="none" w="52px"  onClick={() => onToggleSort("position")} color={sortKey === "position" ? TOP_BAR_BLUE : undefined}>Pos{sortInd("position")}</Th>
                      <Th isNumeric cursor="pointer" userSelect="none" w="48px"  onClick={() => onToggleSort("games")} color={sortKey === "games" ? TOP_BAR_BLUE : undefined}>G{sortInd("games")}</Th>
                      <Th isNumeric cursor="pointer" userSelect="none" w="52px"  onClick={() => onToggleSort("at_bats")} color={sortKey === "at_bats" ? TOP_BAR_BLUE : undefined}>AB{sortInd("at_bats")}</Th>
                      <Th isNumeric cursor="pointer" userSelect="none" w="52px"  onClick={() => onToggleSort("hrs")} color={sortKey === "hrs" ? TOP_BAR_BLUE : undefined}>HR{sortInd("hrs")}</Th>
                      <Th isNumeric cursor="pointer" userSelect="none" w="60px"  onClick={() => onToggleSort("batting_avg")} color={sortKey === "batting_avg" ? TOP_BAR_BLUE : undefined}>AVG{sortInd("batting_avg")}</Th>
                      <Th isNumeric cursor="pointer" userSelect="none" w="60px"  onClick={() => onToggleSort("slugging_pct")} color={sortKey === "slugging_pct" ? TOP_BAR_BLUE : undefined}>SLG{sortInd("slugging_pct")}</Th>
                      <Th w="60px"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {players.length === 0 ? (
                      <Tr>
                        <Td colSpan={9}>
                          <Text color="gray.400" py={4} textAlign="center">No players found.</Text>
                        </Td>
                      </Tr>
                    ) : players.map((player) => {
                      const s = tabIdx === 0 ? player.prev_season : player.spring_training;
                      const isSelected = selectedIdsSet.has(player.id);
                      const isFull = selectedIdsLength >= ROSTER_SIZE && !isSelected;
                      return (
                        <Tr
                          key={player.id}
                          bg={isSelected ? "blue.50" : "white"}
                          _hover={{ bg: isSelected ? "blue.100" : "gray.50" }}
                          cursor={isFull ? "default" : "pointer"}
                          opacity={isFull ? 0.45 : 1}
                          onClick={() => !isFull && onTogglePlayer(player)}
                        >
                          <Td fontWeight={isSelected ? "bold" : "medium"}>
                            {player.name}
                            {isSelected && <Badge ml={2} colorScheme="blue" fontSize="2xs">PICKED</Badge>}
                          </Td>
                          <Td fontWeight="semibold" fontSize="xs">{player.team || "—"}</Td>
                          <Td fontSize="xs" color="gray.500">{player.position || "—"}</Td>
                          <Td isNumeric>{s?.games ?? "—"}</Td>
                          <Td isNumeric>{s?.at_bats ?? "—"}</Td>
                          <Td isNumeric fontWeight="bold" color={TOP_BAR_BLUE}>{s?.home_runs ?? "—"}</Td>
                          <Td isNumeric fontSize="xs">{fmtAvg(s?.batting_avg)}</Td>
                          <Td isNumeric fontSize="xs">{fmtSlg(s?.slugging_pct)}</Td>
                          <Td>
                            {isSelected ? (
                              <Button size="xs" colorScheme="red" variant="outline"
                                onClick={(e) => { e.stopPropagation(); onRemovePlayer(player.id); }}>
                                Remove
                              </Button>
                            ) : (
                              <Button size="xs" colorScheme="blue" variant="outline" isDisabled={isFull}
                                onClick={(e) => { e.stopPropagation(); onTogglePlayer(player); }}>
                                Add
                              </Button>
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>

              {/* Footer with pagination */}
              <Box px={4} py={2} bg="gray.50" borderTop="1px solid" borderColor="gray.100" flexShrink={0}>
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.500">
                    {totalCount} player{totalCount !== 1 ? "s" : ""}
                    {activeTab === 0
                      ? ` · Cap uses ${prevYearLabel} HRs`
                      : ` · Spring training stats are informational only`}
                  </Text>
                  {totalPages > 1 && (
                    <HStack spacing={1}>
                      <Button size="xs" variant="ghost" isDisabled={page === 1} onClick={() => onPageChange(1)}>«</Button>
                      <Button size="xs" variant="ghost" isDisabled={page === 1} onClick={() => onPageChange(page - 1)}>‹</Button>
                      <Text fontSize="xs" color="gray.600" px={1}>{page} / {totalPages}</Text>
                      <Button size="xs" variant="ghost" isDisabled={page === totalPages} onClick={() => onPageChange(page + 1)}>›</Button>
                      <Button size="xs" variant="ghost" isDisabled={page === totalPages} onClick={() => onPageChange(totalPages)}>»</Button>
                    </HStack>
                  )}
                </HStack>
              </Box>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
});

// ── Main page component ───────────────────────────────────────────────────────
export default function MlbHrDerbyDraftPage() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const { poolId }       = useParams();
  const numericPoolId    = Number(poolId);


  // ── Page data ─────────────────────────────────────────────────────────────
  const [season,      setSeason]      = useState(null);
  const [allPlayers,  setAllPlayers]  = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [pageError,   setPageError]   = useState("");
  const [existingRosters, setExistingRosters] = useState([]);

  // ── Roster edit/delete state ──────────────────────────────────────────────
  const [draftEditRosterId, setDraftEditRosterId] = useState(null); // roster loaded into form for editing
  const [deletingRosterId,  setDeletingRosterId]  = useState(null);
  const [confirmDeleteId,   setConfirmDeleteId]   = useState(null);
  const draftFormRef = useRef(null);

  // ── Draft state ───────────────────────────────────────────────────────────
  const [teamName,      setTeamName]      = useState("");
  const [selectedIds,   setSelectedIds]   = useState([]);
  const [submitting,    setSubmitting]    = useState(false);
  const [submitError,   setSubmitError]   = useState("");
  const [justSubmitted, setJustSubmitted] = useState(null);

  // ── Table controls ────────────────────────────────────────────────────────
  const [activeTab,   setActiveTab]   = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey,     setSortKey]     = useState("hrs");
  const [sortDir,     setSortDir]     = useState("desc");
  const [page,        setPage]        = useState(1);

  // ── Height matching ───────────────────────────────────────────────────────
  const leftColRef = useRef(null);
  const [leftColHeight, setLeftColHeight] = useState(null);

  useLayoutEffect(() => {
    if (loadingPage) return;
    const el = leftColRef.current;
    if (!el) return;
    const update = () => setLeftColHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loadingPage]); // re-runs when full layout mounts (loadingPage false)

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    async function load() {
      try {
        const s = await getActiveSeason();
        setSeason(s);
        // getMlbPool's result is unused, but the call is kept: it rejects for
        // non-members, so it doubles as the page's access check.
        const [, players, rosters] = await Promise.all([
          getMlbPool(s.id, numericPoolId),
          getMlbPlayers(s.id),
          getMyMlbRosters(s.id, numericPoolId),
        ]);
        setAllPlayers(players);
        setExistingRosters(rosters);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Unable to load page.";
        setPageError(typeof msg === "string" ? msg : "Unable to load page.");
      } finally {
        setLoadingPage(false);
      }
    }
    load();
  }, [numericPoolId]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const selectedPlayers = useMemo(
    () => selectedIds.map((id) => allPlayers.find((p) => p.id === id)).filter(Boolean),
    [selectedIds, allPlayers]
  );

  const totalPrevHrs = useMemo(
    () => selectedPlayers.reduce((s, p) => s + (p.prev_season?.home_runs ?? 0), 0),
    [selectedPlayers]
  );

  const barPct = Math.min((totalPrevHrs / HR_CAP) * 100, 100);
  const barColor =
    totalPrevHrs > HR_CAP  ? "#e53e3e" :
    totalPrevHrs >= 120    ? "#dd6b20" :
    totalPrevHrs >= 100    ? "#d69e2e" :
                             "#38a169";

  const displayedPlayers = useMemo(() => {
    function getStat(player, key) {
      const s = activeTab === 0 ? player.prev_season : player.spring_training;
      if (!s) return 0;
      switch (key) {
        case "hrs":          return s.home_runs ?? 0;
        case "games":        return s.games ?? 0;
        case "at_bats":      return s.at_bats ?? 0;
        case "batting_avg":  return s.batting_avg ?? 0;
        case "slugging_pct": return s.slugging_pct ?? 0;
        default:             return 0;
      }
    }
    let list = [...allPlayers];
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.team || "").toLowerCase().includes(q) ||
          (p.position || "").toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => {
      if (sortKey === "name") {
        const cmp = a.name.localeCompare(b.name);
        return sortDir === "desc" ? -cmp : cmp;
      }
      if (sortKey === "team") {
        const cmp = (a.team || "").localeCompare(b.team || "");
        return sortDir === "desc" ? -cmp : cmp;
      }
      if (sortKey === "position") {
        const cmp = (a.position || "").localeCompare(b.position || "");
        return sortDir === "desc" ? -cmp : cmp;
      }
      const aVal = getStat(a, sortKey);
      const bVal = getStat(b, sortKey);
      return sortDir === "desc" ? bVal - aVal : aVal - bVal;
    });
  }, [allPlayers, searchQuery, sortKey, sortDir, activeTab]);

  // Reset to page 1 whenever the filtered/sorted list changes
  useEffect(() => setPage(1), [displayedPlayers]);

  const pagedPlayers = useMemo(() => {
    const start = (page - 1) * TABLE_PAGE_SIZE;
    return displayedPlayers.slice(start, start + TABLE_PAGE_SIZE);
  }, [displayedPlayers, page]);

  const totalPages = Math.max(1, Math.ceil(displayedPlayers.length / TABLE_PAGE_SIZE));

  // ── Stable callbacks (functional setState — no deps needed) ───────────────
  const handleTogglePlayer = useCallback((player) => {
    setSelectedIds((prev) => {
      if (prev.includes(player.id)) return prev.filter((id) => id !== player.id);
      if (prev.length >= ROSTER_SIZE) return prev;
      return [...prev, player.id];
    });
    setSubmitError("");
  }, []);

  const handleRemovePlayer = useCallback((id) => {
    setSelectedIds((prev) => prev.filter((pid) => pid !== id));
    setSubmitError("");
  }, []);

  const handleToggleSort = useCallback((key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "desc" ? "asc" : "desc"));
        return prev;
      }
      setSortDir("desc");
      return key;
    });
  }, []);

  const handleSearchChange = useCallback((e) => setSearchQuery(e.target.value), []);

  const handleTabChange = useCallback((i) => {
    setActiveTab(i);
    setSortKey("hrs");
    setSortDir("desc");
  }, []);

  const handlePageChange = useCallback((p) => setPage(p), []);

  // ── Submit ────────────────────────────────────────────────────────────────
  async function handleSubmit() {
    setSubmitError("");
    if (!teamName.trim()) { setSubmitError("Team name is required."); return; }
    if (selectedIds.length !== ROSTER_SIZE) { setSubmitError(`Select exactly ${ROSTER_SIZE} players.`); return; }
    if (totalPrevHrs > HR_CAP) { setSubmitError(`Roster exceeds the ${HR_CAP} HR cap. Current total: ${totalPrevHrs} HRs.`); return; }

    setSubmitting(true);
    try {
      const payload = { team_name: teamName.trim(), player_ids: selectedIds };
      if (draftEditRosterId) {
        await updateMlbRoster(season.id, numericPoolId, draftEditRosterId, payload);
        setJustSubmitted(teamName.trim());
        setDraftEditRosterId(null);
      } else {
        await createMlbRoster(season.id, numericPoolId, payload);
        setJustSubmitted(teamName.trim());
      }
      const updated = await getMyMlbRosters(season.id, numericPoolId);
      setExistingRosters(updated);
      setTeamName("");
      setSelectedIds([]);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to submit roster.";
      setSubmitError(typeof msg === "string" ? msg : "Failed to submit roster.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleLoadForEdit(roster) {
    setDraftEditRosterId(roster.id);
    setTeamName(roster.team_name);
    setSelectedIds(roster.players.map((p) => p.player_id));
    setSubmitError("");
    setJustSubmitted(null);
    setConfirmDeleteId(null);
    draftFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function handleCancelEdit() {
    setDraftEditRosterId(null);
    setTeamName("");
    setSelectedIds([]);
    setSubmitError("");
  }

  async function handleDeleteRoster(rosterId) {
    setDeletingRosterId(rosterId);
    try {
      await deleteMlbRoster(season.id, numericPoolId, rosterId);
      const updated = await getMyMlbRosters(season.id, numericPoolId);
      setExistingRosters(updated);
    } catch (err) {
      // surface error via pageError or just log; delete is straightforward
      setPageError(err?.response?.data?.detail || "Failed to delete team.");
    } finally {
      setDeletingRosterId(null);
      setConfirmDeleteId(null);
    }
  }

  function handleLogout() { logout(); navigate("/login", { replace: true }); }

  // ── Render ────────────────────────────────────────────────────────────────
  if (loadingPage) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" />
          <Text>Loading draft page...</Text>
        </Flex>
      </Box>
    );
  }

  const prevYearLabel = season?.prev_year ?? "Previous";
  const currYearLabel = season?.year ?? "Current";

  const rosterLocked = (() => {
    if (!season?.roster_lock_date) return false;
    const lock = new Date(season.roster_lock_date);
    lock.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today >= lock;
  })();

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">

      <PageHeader
        title="MLB HR DERBY"
        onBack={() => navigate("/mlb-hr-derby")}
        user={user}
        onLogout={handleLogout}
      />

      {/* ── Page Body ── */}
      <Container maxW="container.xl" py={8}>
        {pageError && (
          <Alert status="error" borderRadius="md" mb={6}><AlertIcon />{pageError}</Alert>
        )}
        {justSubmitted && (
          <Alert status="success" borderRadius="md" mb={6}>
            <AlertIcon />
            <strong>{justSubmitted}</strong>&nbsp;submitted! Draft another team below, or&nbsp;
            <Button variant="link" colorScheme="green" size="sm" onClick={() => navigate(`/mlb-hr-derby/current/${numericPoolId}`)}>
              go to the leaderboard
            </Button>.
          </Alert>
        )}

        {/* ── Draft Rules ── */}
        <Box bg="orange.100" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6} mb={6}>
          <Heading size="lg" color={TOP_BAR_BLUE} mb={4}>Draft Rules</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box>
              <Text fontWeight="bold" fontSize="md" color={TOP_BAR_BLUE} mb={1}>Roster Construction</Text>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm">· Pick exactly <strong>{ROSTER_SIZE} players</strong></Text>
                <Text fontSize="sm">· Combined <strong>{prevYearLabel} HR total must be ≤ {HR_CAP}</strong></Text>
              </VStack>
            </Box>
            <Box>
              <Text fontWeight="bold" fontSize="md" color={TOP_BAR_BLUE} mb={1}>Teams &amp; Entries</Text>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm">· Each team name must be <strong>unique in the pool</strong></Text>
                <Text fontSize="sm">· You may submit <strong>multiple entries</strong> per pool</Text>
              </VStack>
            </Box>
            <Box>
              <Text fontWeight="bold" fontSize="md" color={TOP_BAR_BLUE} mb={1}>In-Season Swap</Text>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm">· <strong>One roster swap</strong> is allowed per entry</Text>
                <Text fontSize="sm">· Replacement must have <strong>≤ HRs than the player dropped</strong> (prior season and current season at time of swap)</Text>
              </VStack>
            </Box>
          </SimpleGrid>
        </Box>

        {/* ── Two-column body ── */}
        <Flex gap={6} flexWrap={{ base: "wrap", xl: "nowrap" }}>

          {/* ── LEFT: Draft form + Your Teams ── */}
          <Box ref={leftColRef} flex="2" minW="300px" maxW={{ base: "100%", xl: "400px" }}>
            <VStack spacing={5} align="stretch">

              {/* Draft form */}
              <Box ref={draftFormRef} bg="white" color="black" border={`3px solid ${draftEditRosterId ? "#b7791f" : TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6}>
                <HStack justify="space-between" align="center" mb={4}>
                  <Heading size="md" color={draftEditRosterId ? "orange.600" : TOP_BAR_BLUE}>
                    {draftEditRosterId ? "Edit Team" : "Draft Your Team"}
                  </Heading>
                  {draftEditRosterId && (
                    <Button size="xs" variant="ghost" color="gray.500" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  )}
                </HStack>

                <FormControl mb={4} isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Team Name</FormLabel>
                  <Input
                    placeholder="Enter a unique team name..."
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    borderColor="gray.300"
                    _focus={{ borderColor: TOP_BAR_BLUE, boxShadow: `0 0 0 1px ${TOP_BAR_BLUE}` }}
                  />
                </FormControl>

                <FormLabel fontSize="sm" fontWeight="semibold" mb={2}>
                  Selected Players ({selectedIds.length}/{ROSTER_SIZE})
                </FormLabel>
                <VStack align="stretch" spacing={1} mb={4}>
                  {Array.from({ length: ROSTER_SIZE }).map((_, i) => {
                    const player = selectedPlayers[i];
                    const prevHrs = player?.prev_season?.home_runs ?? 0;
                    return (
                      <Flex
                        key={i}
                        align="center" justify="space-between"
                        px={3} py={2} borderRadius="md"
                        bg={player ? "blue.50" : "gray.50"}
                        border="1px solid"
                        borderColor={player ? "blue.200" : "gray.200"}
                        minH="40px"
                      >
                        {player ? (
                          <>
                            <Box flex="1" minW="0">
                              <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{player.name}</Text>
                              <Text fontSize="xs" color="gray.500">{player.team || "—"} · {prevHrs} HRs</Text>
                            </Box>
                            <IconButton
                              icon={<CloseIcon boxSize="8px" />}
                              size="xs" variant="ghost" colorScheme="red"
                              aria-label="Remove player" ml={2}
                              onClick={() => handleRemovePlayer(player.id)}
                            />
                          </>
                        ) : (
                          <Text fontSize="sm" color="gray.400">Slot {i + 1} — pick from the table</Text>
                        )}
                      </Flex>
                    );
                  })}
                </VStack>

                {/* HR Cap bar */}
                <Box mb={4}>
                  <Flex justify="space-between" align="baseline" mb={1}>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600">{prevYearLabel} HR Total</Text>
                    <Text fontSize="sm" fontWeight="bold" color={totalPrevHrs > HR_CAP ? "red.600" : totalPrevHrs >= 120 ? "orange.500" : "gray.700"}>
                      {totalPrevHrs} / {HR_CAP}
                    </Text>
                  </Flex>
                  <Box h="12px" bg="gray.100" borderRadius="full" overflow="hidden">
                    <Box h="100%" w={`${barPct}%`} bg={barColor} borderRadius="full" transition="width 0.2s, background-color 0.2s" />
                  </Box>
                  <Flex justify="space-between" mt={1}>
                    <Text fontSize="2xs" color="gray.400">0</Text>
                    <Text fontSize="2xs" color={totalPrevHrs >= 120 ? "orange.500" : "gray.400"}>{HR_CAP} cap</Text>
                  </Flex>
                </Box>

                {totalPrevHrs > HR_CAP && (
                  <Alert status="error" borderRadius="md" mb={3} py={2}>
                    <AlertIcon />
                    <Text fontSize="sm">Over the {HR_CAP} HR cap by {totalPrevHrs - HR_CAP} HRs. Remove a player.</Text>
                  </Alert>
                )}
                {totalPrevHrs > 0 && totalPrevHrs <= HR_CAP && (
                  <Text fontSize="xs" color="gray.500" mb={3}>{HR_CAP - totalPrevHrs} HRs remaining under cap</Text>
                )}
                {submitError && (
                  <Alert status="error" borderRadius="md" mb={3} py={2}>
                    <AlertIcon /><Text fontSize="sm">{submitError}</Text>
                  </Alert>
                )}

                <Button
                  w="100%"
                  bg={draftEditRosterId ? "orange.500" : TOP_BAR_BLUE}
                  color="white"
                  _hover={{ bg: draftEditRosterId ? "orange.600" : "#00004a" }}
                  isLoading={submitting}
                  loadingText={draftEditRosterId ? "Saving..." : "Submitting..."}
                  isDisabled={selectedIds.length !== ROSTER_SIZE || totalPrevHrs > HR_CAP || !teamName.trim()}
                  onClick={handleSubmit}
                >
                  {draftEditRosterId ? "Save Changes" : "Submit Team"}
                </Button>
              </Box>

              {/* Your submitted teams */}
              {existingRosters.length > 0 && (
                <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6}>
                  <HStack justify="space-between" mb={3}>
                    <Heading size="sm" color={TOP_BAR_BLUE}>Your Teams ({existingRosters.length})</Heading>
                    {rosterLocked && (
                      <Text fontSize="xs" color="gray.400">Season started — rosters locked</Text>
                    )}
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    {existingRosters.map((r) => {
                      const rosterTotal = (r.players || []).reduce((s, p) => s + (p.prev_hrs ?? 0), 0);
                      const isDeleting  = deletingRosterId === r.id;
                      const confirming  = confirmDeleteId === r.id;
                      const isActiveEdit = draftEditRosterId === r.id;
                      return (
                        <Box key={r.id} p={3} bg={isActiveEdit ? "orange.50" : "green.50"} borderRadius="md" border="1px solid"
                          borderColor={confirming ? "red.300" : isActiveEdit ? "orange.300" : "green.200"}>
                          {/* Header row */}
                          <HStack justify="space-between" mb={1} align="center">
                            <HStack spacing={2} flex="1" minW="0">
                              <CheckIcon color={isActiveEdit ? "orange.400" : "green.500"} boxSize={3} flexShrink={0} />
                              <Text fontSize="sm" fontWeight="bold" noOfLines={1}>{r.team_name}</Text>
                              {isActiveEdit && <Badge colorScheme="orange" fontSize="2xs">Editing</Badge>}
                            </HStack>
                            <HStack spacing={1} flexShrink={0}>
                              <Badge colorScheme="blue" fontSize="2xs">{rosterTotal} HRs</Badge>
                              {!rosterLocked && !confirming && (
                                <>
                                  <Tooltip label="Edit team" hasArrow>
                                    <IconButton
                                      icon={<EditIcon />} size="xs" variant="ghost"
                                      colorScheme={isActiveEdit ? "orange" : "gray"}
                                      aria-label="Edit team"
                                      onClick={() => handleLoadForEdit(r)}
                                    />
                                  </Tooltip>
                                  <Tooltip label="Delete team" hasArrow>
                                    <IconButton
                                      icon={<DeleteIcon />} size="xs" variant="ghost"
                                      colorScheme="red" aria-label="Delete team"
                                      onClick={() => { setConfirmDeleteId(r.id); if (isActiveEdit) handleCancelEdit(); }}
                                    />
                                  </Tooltip>
                                </>
                              )}
                            </HStack>
                          </HStack>

                          {/* Delete confirmation */}
                          {confirming && (
                            <HStack mt={2} spacing={2}>
                              <Text fontSize="xs" color="red.600" flex="1">Delete <strong>{r.team_name}</strong>?</Text>
                              <Button size="xs" colorScheme="red" isLoading={isDeleting}
                                onClick={() => handleDeleteRoster(r.id)}>Delete</Button>
                              <Button size="xs" variant="ghost"
                                onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                            </HStack>
                          )}

                          {/* Player list */}
                          {!confirming && (
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              {(r.players || []).map((p) => p.name).join(", ")}
                            </Text>
                          )}
                        </Box>
                      );
                    })}
                  </VStack>
                  <Button
                    mt={4} w="100%" variant="outline"
                    borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE} size="sm"
                    _hover={{ bg: "gray.50" }}
                    onClick={() => navigate(`/mlb-hr-derby/current/${numericPoolId}`)}
                  >
                    Go to Leaderboard
                  </Button>
                </Box>
              )}
            </VStack>
          </Box>

          {/* ── RIGHT: Player browser ── */}
          <PlayerBrowserPanel
            players={pagedPlayers}
            totalCount={displayedPlayers.length}
            selectedIdsSet={selectedIdsSet}
            selectedIdsLength={selectedIds.length}
            activeTab={activeTab}
            prevYearLabel={prevYearLabel}
            currYearLabel={currYearLabel}
            sortKey={sortKey}
            sortDir={sortDir}
            searchQuery={searchQuery}
            page={page}
            totalPages={totalPages}
            height={leftColHeight}
            onTogglePlayer={handleTogglePlayer}
            onRemovePlayer={handleRemovePlayer}
            onToggleSort={handleToggleSort}
            onSearchChange={handleSearchChange}
            onTabChange={handleTabChange}
            onPageChange={handlePageChange}
          />

        </Flex>
      </Container>
    </Box>
  );
}
